import { useEffect, useRef, useState } from 'react';

/**
 * Hook para countdown baseado em timestamp
 * Calcula o tempo restante baseado no momento que recebeu o valor inicial,
 * evitando problemas de delay na comunicação
 *
 * @param {number|null} initialSeconds - Segundos iniciais do countdown (null para desativar)
 * @param {boolean} isActive - Se o countdown está ativo
 * @param {function} onComplete - Callback quando countdown chega a zero (opcional)
 * @param {number|null} receivedTimestamp - Timestamp de quando recebeu o valor (opcional, usa Date.now() se não fornecido)
 * @returns {number} Segundos restantes (arredondado para baixo)
 */
export default function useCountdown(initialSeconds, isActive = true, onComplete = null, receivedTimestamp = null) {
  const [seconds, setSeconds] = useState(0);
  const startTimeRef = useRef(null);
  const initialSecondsRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Mantém onCompleteRef atualizado
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Quando recebe novo valor inicial, guarda o timestamp
  useEffect(() => {
    if (initialSeconds != null && initialSeconds > 0 && isActive) {
      // Usa timestamp recebido se disponível, senão usa Date.now()
      startTimeRef.current = receivedTimestamp || Date.now();
      initialSecondsRef.current = initialSeconds;
      hasCompletedRef.current = false;
      setSeconds(initialSeconds);
    } else if (!isActive || initialSeconds == null) {
      startTimeRef.current = null;
      initialSecondsRef.current = null;
      hasCompletedRef.current = false;
      setSeconds(0);
    }
  }, [initialSeconds, isActive, receivedTimestamp]);

  // Atualiza o countdown a cada segundo baseado no timestamp
  useEffect(() => {
    if (!isActive || !startTimeRef.current || !initialSecondsRef.current) {
      return;
    }

    const updateCountdown = () => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, initialSecondsRef.current - elapsed);
      setSeconds(remaining);

      // Chama callback quando chega a zero (apenas uma vez)
      if (remaining === 0 && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    };

    // Atualiza imediatamente
    updateCountdown();

    // Atualiza a cada segundo
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isActive, initialSeconds]);

  return seconds;
}

